import type { Note } from "./schemas";

/** Escape text for HTML. */
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * The page a note prints as (PDF export on web and mobile). `contentHtml` is the note's body, already
 * safe HTML: the email's HTML, rendered Markdown, or escaped plain text in <div class="plain">.
 * Pictures not placed in the text come after it; videos become links.
 */
export function printDocument(
  note: Note,
  contentHtml: string,
  extras: { pictures?: Array<{ url: string; name: string | null }>; videos?: Array<{ url: string; name: string | null }> } = {},
): string {
  const esc = escapeHtml;
  const pictures = (extras.pictures ?? [])
    .map((p) => `<figure><img src="${esc(p.url)}" alt="${esc(p.name ?? "")}"><figcaption>${esc(p.name ?? "")}</figcaption></figure>`)
    .join("");
  const videos = (extras.videos ?? []).map((v) => `<li>🎬 <a href="${esc(v.url)}">${esc(v.name ?? "video")}</a></li>`).join("");
  const more = (pictures ? `<div class="pictures">${pictures}</div>` : "") + (videos ? `<ul class="videos">${videos}</ul>` : "");
  const meta = [
    new Date(note.createdAt).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" }),
    note.categories.length ? note.categories.map((c) => `#${c}`).join(" ") : null,
    note.fromAddr ? `From ${note.fromAddr}` : null,
  ]
    .filter(Boolean)
    .map((s) => esc(s!))
    .join(" · ");

  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; style-src 'unsafe-inline'; font-src https: data:"><title>${esc(note.title || "Note")}</title><style>
    @page { margin: 18mm 16mm; }
    body { font: 11pt/1.55 -apple-system, "Segoe UI", Roboto, "PingFang SC", "Noto Sans CJK SC", sans-serif; color: #1f2328; margin: 0; }
    h1.title { font-size: 20pt; margin: 0 0 4pt; }
    .meta { color: #667085; font-size: 9pt; margin: 0 0 14pt; padding-bottom: 8pt; border-bottom: 1px solid #e2e5ea; }
    .plain { white-space: pre-wrap; overflow-wrap: anywhere; }
    img { max-width: 100%; }
    .pictures figure { margin: 14pt 0 0; }
    .pictures img { display: block; border: 1px solid #e2e5ea; border-radius: 4pt; }
    figcaption { color: #98a2b3; font-size: 8pt; margin-top: 3pt; }
    .videos { margin-top: 12pt; padding-left: 0; list-style: none; }
    pre { background: #f2f4f8; padding: 8pt; border-radius: 4pt; white-space: pre-wrap; }
    code { font: 9.5pt ui-monospace, Menlo, monospace; }
    blockquote { margin: 0 0 8pt; padding: 0 10pt; border-left: 3px solid #2f5bea; color: #555; }
    table { border-collapse: collapse; } th, td { border: 1px solid #ccc; padding: 3pt 6pt; }
    li:has(> input[type=checkbox]) { list-style: none; margin-left: -1.2em; }
    a { color: #2f5bea; }
    .footer { margin-top: 24pt; color: #98a2b3; font-size: 8pt; }
  </style></head><body><h1 class="title">${esc(note.title || "Untitled")}</h1><p class="meta">${meta}</p>${contentHtml}${more}<p class="footer">Exported from ccnote</p></body></html>`;
}
